import {
  Icon,
  IMG,
  PageHero,
  Reveal,
  ImageBand,
  Section,
  SectionHeader,
  TextLink,
  FinalCta,
} from "../lib/shared"
import { PROCESS, STUDIO_STATS } from "../lib/data"
import { Seo, breadcrumbSchema, organizationSchema } from "../lib/seo"
import { SITE } from "../lib/site"

const VALUES = [
  {
    icon: "strategy" as const,
    t: "Value before code",
    d: "We agree what the work should save or earn before anything gets built. That number shapes every decision after it.",
  },
  {
    icon: "design" as const,
    t: "Made for the people using it",
    d: "An internal tool deserves the same care as a customer app. If your team has to work around it, it isn't finished.",
  },
  {
    icon: "tech" as const,
    t: "The right tool for the job",
    d: "We use AI where it pays for itself and plain software where that's the better answer.",
  },
  {
    icon: "future" as const,
    t: "Built to grow with you",
    d: "Your business will change. What we build should adapt with it rather than need replacing.",
  },
]

const TEAM = [
  { n: "Habeeb Suleiman", r: "Founder & Creative Director" },
  { n: "Ada Nwosu", r: "Head of Strategy" },
  { n: "Tunde Ige", r: "Lead Designer" },
  { n: "Grace Bello", r: "Engineering Lead" },
]

export default function About() {
  return (
    <>
      <Seo
        title="About the studio"
        description="Sableboxx is a software, automation and brand studio in Abuja, Nigeria. We build web applications, automate manual work and shape the brands around them."
        jsonLd={[
          organizationSchema(),
          breadcrumbSchema([{ name: "About", path: "/about" }]),
        ]}
      />
      <PageHero
        centered
        eyebrow="About us"
        title="A studio built on one idea: technology should pay for itself"
        intro="Sableboxx is a software, automation and brand studio in Abuja. Engineering and design work as one team here, so the businesses we work with run better, move faster and look the part."
        image={IMG.aboutHero}
      />

      {/*
        Who we are.

        Three columns on desktop — founded, mission, the longer story — each
        under a small uppercase label, widening left to right as the copy gets
        longer. Below lg they stack, separated by hairlines.
      */}
      <Section tone="paper">
        <div className="grid grid-cols-1 divide-y divide-neutral-200 lg:grid-cols-12 lg:gap-x-12 lg:divide-y-0">
          <Reveal className="pb-10 lg:col-span-2 lg:pb-0">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.18em] text-accent">
              Founded
            </h2>
            <p className="mt-5 space-y-6 text-[16px] leading-[1.8] text-black">
              {SITE.foundingYear}
            </p>
          </Reveal>

          <Reveal delay={80} className="py-10 lg:col-span-4 lg:py-0">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.18em] text-accent">
              Our mission
            </h2>
            <p className="mt-5 space-y-6 text-[16px] leading-[1.8] text-black">
              To make better technology accessible to more businesses. We want to see more 
              businesses use technology properly, not because it's fashionable, but because 
              it can genuinely improve the way they work, serve people and grow.
            </p>
          </Reveal>

          <Reveal delay={160} className="pt-10 lg:col-span-6 lg:pt-0">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.18em] text-accent">
              Our Story
            </h2>
            <div className="mt-5 space-y-6 text-[16px] leading-[1.8] text-black">
              <p>SableBoxx began in 2023 with a focus on creative work — helping businesses with branding, websites and digital experiences. But the more businesses we worked with, the more we noticed that the visible problem was rarely the whole problem.</p>
              <p>A business might ask for a new website, but behind it was a team managing customers through WhatsApp and spreadsheets. Another might need a better brand, while the real challenge was that customers could not easily understand what the business offered. Others were spending hours every week doing work that could have been handled by a simple system.</p>
              <p>We started looking beyond the brief.</p>
              <p>Instead of only designing the website, we started asking how the business worked behind it. Instead of simply recommending another tool, we started building the systems that businesses actually needed. Over time, software development and automation became a bigger part of what we do, while design remained at the heart of how we approach the work.</p>
              <p>That is how SableBoxx grew into what it is today: a studio bringing <strong>software, automation and brand</strong> together.</p>
              <p>We have worked with businesses at different stages and across different industries, and no two projects have looked exactly the same. Some have started with a rough idea. Others have come to us with a business that was already working but needed better systems, a stronger digital presence or a more efficient way of doing things.</p>
              <p>What has stayed consistent is the way we approach the work. We take the time to understand the business before deciding what to build. We question things that don't make sense. We keep the technology appropriate to the problem. And we stay close to the work instead of creating layers between the client and the people actually building it.</p>
              <p>We are still growing, and we are still learning. But the reason SableBoxx exists has become clearer with every project: <strong>there are good businesses with big ambitions that simply need better tools to get there.</strong></p>
              <p>We want to be one of the teams they can turn to when they're ready to build those tools.</p>
              <p>That is the story so far. And we're only getting started.</p>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* In numbers */}
      <section className="bg-ink text-white">
        <div className="mx-auto max-w-[1280px] px-6 py-20 lg:px-10 lg:py-16">
          <div className="grid grid-cols-2 gap-x-6 gap-y-12 lg:grid-cols-4">
            {STUDIO_STATS.map((s, i) => (
              <Reveal key={s.l} delay={i * 80}>
                <div className="border-l border-white/15 pl-6">
                  <div className="text-[clamp(2.6rem,4.4vw,3.6rem)] font-black leading-none tracking-[-0.03em]">
                    {s.n}
                  </div>
                  <div className="mt-3 text-[14px] text-neutral-400">
                    {s.l}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <Section tone="mist">
        <SectionHeader
          eyebrow="What we believe"
          title="The principles that guide how we think, work and build."
          // intro="Four ideas shape every project we take on, from the first conversation to long after launch."
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {VALUES.map((v, i) => (
            <Reveal key={v.t} delay={(i % 2) * 90}>
              <div className="group flex h-full gap-5 rounded-2xl border border-neutral-200 bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_50px_-30px_rgba(11,16,23,0.3)]">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-ink text-white transition-colors group-hover:bg-accent">
                  <Icon name={v.icon} className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="text-[19px] font-bold tracking-[-0.02em]">
                    {v.t}
                  </h3>
                  <p className="mt-2 text-[15px] leading-[1.7] text-neutral-600">
                    {v.d}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <ImageBand
        tone="mist"
        src={IMG.aboutBand}
        alt="The Sableboxx team planning a project together"
        eyebrow="We’re glad you’re here."
        caption="Thanks for taking the time to get to know SableBoxx and the work we do."
      />

      {/* Process, condensed */}
      <Section tone="paper">
        <SectionHeader
          eyebrow="How we work"
          layout="stacked"
          title="A clear path from idea to launch"
          intro="Every project moves through the same six stages, so you always know where things stand."
          // action={<TextLink to="/process">See the full process</TextLink>}
        />
        <div className="grid grid-cols-1 gap-x-12 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
          {PROCESS.map((p, i) => (
            <Reveal key={p.n} delay={(i % 3) * 90}>
              <div className="border-t border-neutral-200 pt-6">
                <span className="text-[13px] font-black tracking-widest text-accent">
                  {p.n}
                </span>
                <h3 className="mt-3 text-[20px] font-bold tracking-[-0.02em]">
                  {p.t}
                </h3>
                <p className="mt-2 text-[15px] leading-[1.7] text-neutral-600">
                  {p.d}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Team */}
      {/* <Section className="bg-mist py-24 lg:py-32">
        <div className="max-w-2xl">
          <Reveal>
            <EyebrowLabel>The People</EyebrowLabel>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="mt-5 text-[clamp(2rem,3.6vw,3rem)] font-black leading-[1.05] tracking-[-0.03em]">
              Senior by Design.
            </h2>
          </Reveal>
        </div>
        <div className="mt-14 grid grid-cols-2 gap-5 lg:grid-cols-4">
          {TEAM.map((m, i) => (
            <Reveal key={m.n} delay={i * 80}>
              <div className="group">
                <div className="flex aspect-[4/5] items-end overflow-hidden rounded-2xl bg-gradient-to-b from-neutral-200 to-neutral-300 p-5 transition-all duration-500 group-hover:from-ink group-hover:to-ink">
                  <span className="text-[44px] font-black leading-none text-white/60 transition-colors group-hover:text-accent">
                    {m.n
                      .split(" ")
                      .map((w) => w[0])
                      .join("")}
                  </span>
                </div>
                <h3 className="mt-4 text-[16px] font-bold">{m.n}</h3>
                <p className="mt-0.5 text-[13px] text-neutral-500">{m.r}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section> */}

      <FinalCta />
    </>
  )
}
